import { useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { MapPin, Loader2, Eye, EyeOff, CheckCircle2, ArrowLeft } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { useAuth, formatApiError } from "@/context/AuthContext";

export default function ResetPasswordPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  if (user) return <Navigate to="/" replace />;

  if (!token) {
    return (
      <div className="min-h-screen grid place-items-center bg-white px-6">
        <div className="max-w-sm text-center">
          <h2 className="text-2xl font-bold text-zinc-900">Bağlantı geçersiz</h2>
          <p className="mt-2 text-sm text-zinc-500">
            Sıfırlama bağlantısı eksik veya bozuk. Lütfen yeni bir bağlantı isteyin.
          </p>
          <Link
            to="/sifremi-unuttum"
            className="inline-block mt-4 text-sm text-zinc-900 underline underline-offset-4"
            data-testid="request-new-link"
          >
            Yeni bağlantı iste
          </Link>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) {
      toast.error("Şifre en az 8 karakter olmalı");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Şifreler eşleşmiyor");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", {
        token,
        new_password: newPassword,
      });
      setDone(true);
      toast.success("Şifreniz başarıyla yenilendi");
      setTimeout(() => navigate("/giris", { replace: true }), 2000);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-white">
      <div className="hidden lg:block relative overflow-hidden bg-zinc-900">
        <img
          src="https://images.unsplash.com/photo-1635111057505-3b7dcc2b72fb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwzfHxyZWFsJTIwZXN0YXRlJTIwcHJvcGVydHklMjBhYnN0cmFjdHxlbnwwfHx8fDE3ODUwMzM1NTh8MA&ixlib=rb-4.1.0&q=85"
          alt="Ekspertiz"
          className="absolute inset-0 h-full w-full object-cover opacity-80"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-zinc-950/70 via-zinc-900/40 to-transparent" />
        <div className="relative z-10 p-12 h-full flex flex-col justify-between text-white">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 grid place-items-center bg-white text-zinc-900 rounded-sm">
              <MapPin className="h-5 w-5" strokeWidth={2.5} />
            </div>
            <span className="text-xl tracking-tight font-semibold">Kolay Arşivim</span>
          </div>
          <div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
              Yeni şifrenizi <br /> belirleyin.
            </h1>
            <p className="mt-6 text-base text-zinc-200 max-w-md">
              Güvenliğiniz için şifreniz sıfırlandığında tüm cihazlarda mevcut oturumlar sonlandırılır.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center px-8 py-12">
        <div className="w-full max-w-sm">
          <Link
            to="/giris"
            className="inline-flex items-center gap-1.5 text-xs text-zinc-500 hover:text-zinc-900 mb-6"
            data-testid="back-to-login-link"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Giriş sayfasına dön
          </Link>
          <div className="mb-6">
            <h2 className="text-3xl font-bold tracking-tight text-zinc-900">Şifreyi Sıfırla</h2>
            <p className="mt-2 text-sm text-zinc-500">
              Yeni bir şifre belirleyin. Bağlantı tek kullanımlıktır.
            </p>
          </div>

          {done ? (
            <div
              className="border border-emerald-200 bg-emerald-50 rounded-sm p-5"
              data-testid="reset-success"
            >
              <div className="flex items-center gap-2 text-emerald-800 font-semibold">
                <CheckCircle2 className="h-5 w-5" />
                Şifreniz yenilendi
              </div>
              <p className="mt-2 text-sm text-emerald-700 leading-relaxed">
                Giriş sayfasına yönlendiriliyorsunuz...
              </p>
            </div>
          ) : (
            <form className="space-y-5" onSubmit={handleSubmit} data-testid="reset-password-form">
              <div className="space-y-2">
                <Label htmlFor="new-password">Yeni Şifre</Label>
                <div className="relative">
                  <Input
                    id="new-password"
                    type={showPw ? "text" : "password"}
                    required
                    minLength={8}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    data-testid="reset-new-password-input"
                    placeholder="En az 8 karakter"
                    autoFocus
                    className="pr-10"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw((v) => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700"
                    tabIndex={-1}
                    aria-label={showPw ? "Şifreyi gizle" : "Şifreyi göster"}
                  >
                    {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm-password">Yeni Şifre (Tekrar)</Label>
                <Input
                  id="confirm-password"
                  type={showPw ? "text" : "password"}
                  required
                  minLength={8}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  data-testid="reset-confirm-password-input"
                  placeholder="Şifreyi tekrar girin"
                  autoComplete="new-password"
                />
                {confirmPassword && newPassword !== confirmPassword && (
                  <p className="text-[11px] text-red-600" data-testid="reset-mismatch-msg">
                    Şifreler eşleşmiyor.
                  </p>
                )}
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-zinc-900 hover:bg-zinc-800 text-white rounded-sm"
                data-testid="reset-submit-btn"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  "Şifreyi Yenile"
                )}
              </Button>
            </form>
          )}

          <p className="mt-8 text-[11px] text-zinc-400 leading-relaxed">
            Bağlantı çalışmıyorsa süresi dolmuş olabilir.{" "}
            <Link to="/sifremi-unuttum" className="underline hover:text-zinc-900">
              Yeni bağlantı iste
            </Link>
            .
          </p>
        </div>
      </div>
    </div>
  );
}
